/**
 * useAttachmentUpload - Shared file upload queue hook for @f2e/ui
 *
 * This hook provides a Promise-based file upload queue with status tracking,
 * validation, and conversion to FileBadgeData format for QuestionPanel integration.
 *
 * Design principles:
 * - Promise-based uploadFn: Apps provide `uploadFn(file) => Promise<{ uuid, url? }>`
 * - Flexible validation: Built-in defaults with override options
 * - Unified naming: `errorMessage` (not `error`), `url` (not `fileUrl`), `localId` for stable IDs
 * - App-agnostic: No app-specific params; apps inject via uploadFn closure
 */
import { useState, useCallback, useRef, useMemo } from "react";
import { MAX_FILE_SIZE_BYTES, SUPPORTED_MIME_TYPES, } from "@f2e/shared/constants";
import { normalizeMarkdownType } from "@f2e/shared/browser";
import { getFileAttachmentMeta } from "@f2e/shared/utils";
// -------------------------------------------------------------------
// Default configuration
// -------------------------------------------------------------------
const DEFAULT_MAX_FILES = 10;
// Use shared constants from @f2e/shared for consistency with backend
const DEFAULT_MAX_FILE_SIZE = MAX_FILE_SIZE_BYTES;
const DEFAULT_ALLOWED_TYPES = SUPPORTED_MIME_TYPES;
// -------------------------------------------------------------------
// Helper functions
// -------------------------------------------------------------------
/**
 * Generate a unique local ID for tracking
 */
const generateLocalId = () => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};
/**
 * Default duplicate key generator
 */
const defaultGetDuplicateKey = (file) => {
    return `${file.name}:${file.size}:${file.lastModified}`;
};
/**
 * Extension to MIME type mapping for fallback when browser doesn't set MIME type
 * This handles cases where browsers don't properly detect MIME types (e.g., .md files)
 */
const EXTENSION_MIME_MAP = {
    // Text/Document
    md: "text/markdown",
    txt: "text/plain",
    csv: "text/csv",
    json: "application/json",
    // Images
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    svg: "image/svg+xml",
    // Documents
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};
/**
 * Get effective MIME type for a file
 * Falls back to extension-based lookup if browser doesn't set MIME type
 */
const getEffectiveMimeType = (file) => {
    if (file.type)
        return file.type;
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    return EXTENSION_MIME_MAP[ext] || "";
};
/**
 * Check if a MIME type matches an allowed type pattern
 */
const matchesMimeType = (fileType, allowedType) => {
    if (allowedType.endsWith("/*")) {
        const category = allowedType.split("/")[0];
        return fileType.startsWith(`${category}/`);
    }
    return fileType === allowedType;
};
/**
 * Format file size in human-readable format
 */
const formatFileSize = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return `${Math.round(mb)}MB`;
};
// -------------------------------------------------------------------
// Hook implementation
// -------------------------------------------------------------------
export const useAttachmentUpload = (options) => {
    const { uploadFn, maxFiles = DEFAULT_MAX_FILES, maxFileSize = DEFAULT_MAX_FILE_SIZE, allowedTypes = DEFAULT_ALLOWED_TYPES, validateFile: customValidateFile, getFileMeta, getDuplicateKey = defaultGetDuplicateKey, onUploadError, } = options;
    const [files, setFiles] = useState([]);
    // Track which items are currently being uploaded to prevent double uploads
    const uploadingRef = useRef(new Set());
    // Use ref to track current files to avoid stale closure issues
    const filesRef = useRef([]);
    filesRef.current = files;
    // Built-in validation (used if customValidateFile not provided)
    const builtInValidateFile = useCallback((file) => {
        // Check file size
        if (file.size > maxFileSize) {
            return {
                valid: false,
                errorMessage: `File size exceeds ${formatFileSize(maxFileSize)} limit`,
            };
        }
        // Check MIME type (with extension-based fallback for browsers that don't set MIME type)
        const effectiveType = getEffectiveMimeType(file);
        const isTypeAllowed = allowedTypes.some((type) => matchesMimeType(effectiveType, type));
        if (!isTypeAllowed) {
            const ext = file.name.split(".").pop()?.toLowerCase() || "unknown";
            return {
                valid: false,
                errorMessage: `Unsupported file type: .${ext}`,
            };
        }
        return { valid: true };
    }, [maxFileSize, allowedTypes]);
    const validateFile = customValidateFile || builtInValidateFile;
    // Perform single file upload
    const performUpload = useCallback(async (item) => {
        try {
            // Normalize markdown files to ensure correct MIME type for backend
            const normalizedFile = normalizeMarkdownType(item.file);
            const result = await uploadFn(normalizedFile);
            return {
                ...item,
                status: "uploaded",
                uuid: result.uuid,
                url: result.url,
                meta: {
                    ...(item.meta ?? {}),
                    ...(result.meta ?? {}),
                },
            };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Upload failed";
            onUploadError?.({
                type: "upload",
                fileName: item.file.name,
                message: errorMessage,
            });
            return {
                ...item,
                status: "error",
                errorMessage,
            };
        }
    }, [uploadFn, onUploadError]);
    const handleFiles = useCallback(async (fileList) => {
        const currentFiles = filesRef.current;
        const currentFileCount = currentFiles.length;
        const availableSlots = maxFiles - currentFileCount;
        if (availableSlots <= 0) {
            onUploadError?.({
                type: "limit",
                fileName: "",
                message: `Maximum ${maxFiles} files allowed`,
            });
            return;
        }
        // Build set of existing duplicate keys
        const existingKeys = new Set(currentFiles.map((item) => getDuplicateKey(item.file)));
        // Process files and create queue items
        // Iterate through all files until we fill available slots
        const newItems = [];
        const processedKeys = new Set();
        for (const file of fileList) {
            // Stop when we have enough valid files
            if (newItems.length >= availableSlots)
                break;
            const duplicateKey = getDuplicateKey(file);
            // Check for duplicates (in existing files and within current batch)
            if (existingKeys.has(duplicateKey) || processedKeys.has(duplicateKey)) {
                onUploadError?.({
                    type: "validation",
                    fileName: file.name,
                    message: "File already added",
                });
                continue;
            }
            // Validate file
            const validation = validateFile(file);
            if (!validation.valid) {
                onUploadError?.({
                    type: "validation",
                    fileName: file.name,
                    message: validation.errorMessage || "Invalid file",
                });
                continue;
            }
            processedKeys.add(duplicateKey);
            const fileMeta = {
                sizeBytes: file.size,
                ...(getFileAttachmentMeta(file) ?? {}),
                ...((await getFileMeta?.(file)) ?? {}),
            };
            newItems.push({
                localId: generateLocalId(),
                file,
                status: "pending",
                meta: fileMeta,
            });
        }
        if (newItems.length === 0)
            return;
        // Add files with pending status
        setFiles((prev) => [...prev, ...newItems]);
        // Upload files in parallel
        const uploadPromises = newItems.map(async (item) => {
            const { localId } = item;
            // Prevent double uploads
            if (uploadingRef.current.has(localId))
                return;
            uploadingRef.current.add(localId);
            // Set to uploading status
            setFiles((prev) => prev.map((f) => f.localId === localId ? { ...f, status: "uploading" } : f));
            // Perform upload
            const result = await performUpload(item);
            // Update with result - check if file still exists before updating (may have been removed)
            setFiles((prev) => {
                const exists = prev.some((f) => f.localId === localId);
                if (!exists)
                    return prev;
                return prev.map((f) => (f.localId === localId ? result : f));
            });
            uploadingRef.current.delete(localId);
        });
        // Wait for all uploads to complete
        await Promise.all(uploadPromises);
    }, [
        maxFiles,
        validateFile,
        getDuplicateKey,
        getFileMeta,
        performUpload,
        onUploadError,
    ]);
    const removeFile = useCallback((id) => {
        setFiles((prev) => prev.filter((f) => f.localId !== id && f.uuid !== id));
        uploadingRef.current.delete(id);
    }, []);
    const clearFiles = useCallback(() => {
        setFiles([]);
        uploadingRef.current.clear();
    }, []);
    // Convert internal queue items to @f2e/ui FileBadgeData format
    const fileBadges = useMemo(() => files.map((item) => ({
        id: item.uuid || item.localId,
        name: item.file.name,
        type: item.file.type,
        uuid: item.uuid || "",
        url: item.url,
        meta: item.meta,
        status: item.status,
        fileObject: item.file,
    })), [files]);
    // Extract UUIDs from successfully uploaded files
    const uploadedFileUuids = useMemo(() => files
        .filter((f) => f.status === "uploaded" && f.uuid)
        .map((f) => f.uuid), [files]);
    const isUploading = files.some((f) => f.status === "uploading");
    return {
        files,
        fileBadges,
        uploadedFileUuids,
        isUploading,
        handleFiles,
        removeFile,
        clearFiles,
    };
};
export default useAttachmentUpload;
//# sourceMappingURL=useAttachmentUpload.js.map